import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Send, FileSignature, LogOut, Compass } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import LanguageSwitcher from "./LanguageSwitcher";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const links = [
    { to: "/dashboard", label: t("nav.overview"), icon: LayoutDashboard, end: true, key: "overview" },
    { to: "/dashboard/profile", label: t("nav.profile"), icon: Building2, key: "profile" },
    { to: "/dashboard/requests", label: t("nav.requests"), icon: Send, key: "requests" },
    { to: "/dashboard/contracts", label: t("nav.contracts"), icon: FileSignature, key: "contracts" },
  ];

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-bone" data-testid="dashboard-layout">
      <aside className="w-64 shrink-0 bg-midnight text-bone flex flex-col fixed h-screen start-0 z-30">
        <div className="px-6 h-16 flex items-center justify-between border-b border-bone/10">
          <div className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-gold" strokeWidth={1.5} />
            <span className="font-serif text-xl font-bold tracking-tight">Qafila</span>
          </div>
          <LanguageSwitcher variant="dark" />
        </div>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 ${
                  isActive ? "bg-bone/[0.06] text-gold border-s-2 border-gold" : "text-bone/70 hover:text-bone hover:bg-bone/[0.04] border-s-2 border-transparent"
                }`
              }
            >
              <l.icon className="h-4 w-4" strokeWidth={1.5} />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-bone/10">
          <div className="px-2 mb-3">
            <p className="text-sm text-bone truncate">{user?.name}</p>
            <p className="text-xs text-bone/50 truncate">{user?.email}</p>
          </div>
          <button onClick={doLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-bone/70 hover:text-bone hover:bg-bone/[0.04] transition-all duration-200">
            <LogOut className="h-4 w-4 rtl:rotate-180" strokeWidth={1.5} /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 ms-64 min-h-screen mesh-soft">
        <Outlet />
      </main>
    </div>
  );
}
